import { useEffect, useRef, useState } from 'react'
import { ArrowDownWideNarrow } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { EmptyState } from '@/components/ui/empty-state'
import { FilterDropdown } from '@/components/ui/filter-dropdown'
import { Input } from '@/components/ui/input'
import { TimelineDates, timelineDatesLabel } from './TimelineDates'
import {
  familyLabels,
  hasTimelineFilters,
  kindLabel,
  subscribeLocation,
  type TimelineFacets,
  type TimelineFilters,
} from './timelineFilters'

type FilterProps = {
  filters: TimelineFilters
  facets: TimelineFacets
  onChange: (filters: TimelineFilters) => void
  onClear: () => void
  error?: string
}

function TimelineSearch({ filters, onChange }: Pick<FilterProps, 'filters' | 'onChange'>) {
  const [value, setValue] = useState(filters.q)
  const latest = useRef({ filters, onChange })
  latest.current = { filters, onChange }
  useEffect(() => setValue(filters.q), [filters.q])
  useEffect(
    () =>
      subscribeLocation((event) => {
        if (event.state?.contactQueryChange) return
        setValue(new URLSearchParams(window.location.search).get('q') ?? '')
      }),
    [],
  )
  useEffect(() => {
    if (value === latest.current.filters.q) return
    const timer = window.setTimeout(() => {
      latest.current.onChange({ ...latest.current.filters, q: value })
    }, 300)
    return () => window.clearTimeout(timer)
  }, [value])
  return (
    <Input
      aria-label="Search timeline"
      placeholder="Search timeline"
      className="h-9 w-48 max-w-full"
      value={value}
      onChange={(event) => setValue(event.target.value)}
    />
  )
}

function TimelineSort({ filters, onChange }: Pick<FilterProps, 'filters' | 'onChange'>) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button type="button" variant="outline" className="h-9 gap-2">
          <ArrowDownWideNarrow className="size-3.5 text-muted-foreground" />
          {filters.sort === 'oldest' ? 'Oldest first' : 'Newest first'}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onSelect={() => onChange({ ...filters, sort: 'newest' })}>
          Newest first
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => onChange({ ...filters, sort: 'oldest' })}>
          Oldest first
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export function TimelineFilterBar({ filters, facets, onChange, onClear, error }: FilterProps) {
  const kinds = filters.families.length === 1 ? (facets.kinds?.[filters.families[0]] ?? []) : []
  const sources = facets.sources ?? []
  return (
    <div className="space-y-2">
      <div
        role="group"
        aria-label="Timeline filters"
        className="flex min-h-9 flex-wrap items-center gap-2"
      >
        <TimelineSearch filters={filters} onChange={onChange} />
        <FilterDropdown
          label="Family"
          emptyLabel="All families"
          options={Object.entries(familyLabels)
            .filter(([key]) => facets.families.includes(key))
            .map(([value, label]) => ({ value, label }))}
          selected={new Set(filters.families)}
          onChange={(selected) => onChange({ ...filters, families: [...selected], kinds: [] })}
        />
        {kinds.length > 0 && (
          <FilterDropdown
            label="Kind"
            emptyLabel="All kinds"
            options={kinds.map((value) => ({ value, label: kindLabel(value) }))}
            selected={new Set(filters.kinds)}
            onChange={(selected) => onChange({ ...filters, kinds: [...selected] })}
          />
        )}
        {sources.length > 1 && (
          <FilterDropdown
            label="Source"
            emptyLabel="All sources"
            options={sources}
            selected={new Set(filters.owner ? [filters.owner] : [])}
            onChange={(selected) =>
              onChange({
                ...filters,
                owner: [...selected].find((value) => value !== filters.owner) ?? '',
              })
            }
          />
        )}
        <TimelineDates filters={filters} onChange={onChange} error={error} />
        <TimelineSort filters={filters} onChange={onChange} />
        {hasTimelineFilters(filters) && (
          <Button type="button" variant="ghost" onClick={onClear}>
            Clear
          </Button>
        )}
      </div>
      {error && (
        <p role="alert" className="text-caption text-destructive">
          {error}
        </p>
      )}
    </div>
  )
}

function activeLabels(filters: TimelineFilters, facets: TimelineFacets) {
  const labels = filters.families.map((family) => familyLabels[family] ?? family)
  labels.push(...filters.kinds.map(kindLabel))
  if (filters.owner)
    labels.push(
      facets.sources?.find((source) => source.value === filters.owner)?.label ?? filters.owner,
    )
  if (filters.from || filters.to) labels.push(timelineDatesLabel(filters))
  if (filters.q) labels.push(`Search: ${filters.q}`)
  return labels
}

export function TimelineFilteredEmpty({ filters, facets, onClear }: Omit<FilterProps, 'onChange'>) {
  if (!hasTimelineFilters(filters)) return <EmptyState title="Nothing yet" variant="quiet" />
  return (
    <EmptyState
      title="No entries match"
      variant="quiet"
      action={
        <div className="flex flex-col items-center gap-3">
          <div className="flex flex-wrap justify-center gap-1">
            {activeLabels(filters, facets).map((label, index) => (
              <Badge key={index} variant="neutral">
                {label}
              </Badge>
            ))}
          </div>
          <Button type="button" variant="outline" onClick={onClear}>
            Clear filters
          </Button>
        </div>
      }
    />
  )
}
